import axiosInstance from '../../api/axios.js';
import { apiGet, apiPost } from '../../api/oauth/helpers.ts';

export type SourceType = 's3' | 'gcp' | 'zip';
export type OutputFormat = 'docx' | 'pdf';
export type WhisperModel = 'tiny' | 'base' | 'small' | 'medium' | 'large';

export interface S3SourceConfig {
  bucket: string;
  prefix?: string;
  access_key_id: string;
  secret_access_key: string;
  region?: string;
}

export interface GcpSourceConfig {
  bucket: string;
  prefix?: string;
}

export interface ZipSourceConfig {
  gcs_file_ref: string;
}

export interface CreateTranscriptionJobParams {
  sourceType: SourceType;
  sourceConfig: S3SourceConfig | GcpSourceConfig | ZipSourceConfig;
  model?: WhisperModel;
  language?: string;
  formats?: OutputFormat[];
}

export interface TranscriptionJob {
  id: string;
  status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message?: string;
  error_message?: string;
  operation_type: string;
  created_at: string;
  result_data?: {
    output_path: string;
    file_count: number;
    files: Array<{ filename: string; gcs_ref: string }>;
  };
}

export interface TranscriptionOutputFile {
  filename: string;
  gcs_ref: string;
  signed_url: string;
}

/** Upload a ZIP to GCS temp storage. Returns the gcs_file_ref. */
export const uploadZip = async (
  file: File,
  onProgress?: (pct: number) => void
): Promise<{ gcs_file_ref: string }> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post('/api/transcription/upload-zip', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return response.data;
};

/** Create a job record in the Go backend. */
const createJobRecord = async (
  sourceType: SourceType,
  sourceConfig: S3SourceConfig | GcpSourceConfig | ZipSourceConfig
): Promise<{ id: string }> => {
  return apiPost('/api/jobs', {
    operation_type: 'transcription',
    source_type: sourceType,
    source_config: sourceConfig,
  });
};

/** Enqueue the job in the Python worker. */
const enqueueJob = async (
  jobId: string,
  orgId: string,
  params: CreateTranscriptionJobParams
): Promise<void> => {
  await apiPost('/api/transcription/enqueue', {
    job_id: jobId,
    org_id: orgId,
    source_type: params.sourceType,
    source_config: params.sourceConfig,
    model: params.model ?? 'base',
    language: params.language ?? null,
    formats: params.formats ?? ['docx', 'pdf'],
  });
};

/** Full flow: create job record → enqueue. Returns job ID. */
export const startTranscriptionJob = async (
  params: CreateTranscriptionJobParams
): Promise<string> => {
  const orgId = localStorage.getItem('organization_id') ?? '';
  const job = await createJobRecord(params.sourceType, params.sourceConfig);
  await enqueueJob(job.id, orgId, params);
  return job.id;
};

/** Poll job status. */
export const getJob = (jobId: string): Promise<TranscriptionJob> =>
  apiGet(`/api/jobs/${jobId}`);

/** Get signed download URLs for completed job output files. */
export const getJobFiles = (jobId: string): Promise<{ files: TranscriptionOutputFile[] }> =>
  apiGet(`/api/jobs/${jobId}/files`);
